import type { Subject } from './api/endpoints.js';

/**
 * Display helpers shared by anywhere a `Subject` is rendered as a compact
 * row — the subject picker today, possibly the animals list later.
 *
 * Kept separate from `api/endpoints.ts` because these are presentation
 * decisions (what to show when a field is null), not part of the API
 * contract.
 */

/** The name shown for an animal, falling back the same way everywhere. */
export function subjectDisplayName(subject: Subject): string {
  return subject.name ?? subject.commonName ?? subject.scientificName;
}

/** A single letter for the avatar circle. No photos — none are collected. */
export function subjectInitial(subject: Subject): string {
  const name = subjectDisplayName(subject);
  return name.trim().charAt(0).toUpperCase() || '?';
}

/** Whole years since `birthDate`. Approximate by design — see SubjectsPage. */
export function ageYears(birthDate: string): number {
  const born = new Date(birthDate);
  const now = new Date();
  let years = now.getFullYear() - born.getFullYear();
  const beforeBirthdayThisYear =
    now.getMonth() < born.getMonth() ||
    (now.getMonth() === born.getMonth() && now.getDate() < born.getDate());
  if (beforeBirthdayThisYear) years--;
  return Math.max(0, years);
}

/**
 * The breed to show.
 *
 * Prefers the canonical form when the server matched one. That is safe to do
 * precisely because a code is only ever set on an exact match — the two
 * strings differ in capitalisation and spacing and in nothing else, so
 * "border collie" renders as "Border Collie" and no meaning changes. Anything
 * unmatched shows exactly as it was written, which is the whole point: a
 * contributor is never quietly told their animal is something else.
 */
export function subjectBreed(subject: Subject): string | null {
  return subject.breedCode ?? subject.breedOrMorph;
}

/**
 * "Border Collie · 4y" — breed and age if both exist, one alone if only one
 * does, the common name as a last resort, or nothing at all.
 */
export function subjectMeta(subject: Subject): string {
  const age = subject.birthDate ? `${ageYears(subject.birthDate)}y` : null;
  const breed = subjectBreed(subject);
  if (breed && age) return `${breed} · ${age}`;
  if (breed) return breed;
  if (age) return age;
  return subject.commonName ?? '';
}
